// SPDX-License-Identifier: GPL-3.0-or-later
// Copyright (C) 2026 Radiooooo

// The session on screen: the landing versus the frame, and the boot restore.

import { createLifetime } from "../core/lifetime";
import { expectsSession, forgetSession, rememberSession } from "../core/session-marker";
import { preloadMapbox } from "../worldmap/mapbox";
import type { Landing } from "../shell/landing";
import type { Foundation } from "./foundation";
import type { Frame } from "./frame";

/** What the session decides between: the landing, there from the start, and the frame, which it brings. */
export type Screen = {
    landing: Landing;
    frame: {
        /** The frame, fetched and mounted if it is not yet; undefined when it could not be. */
        ensure(): Promise<Frame | undefined>;
        current(): Frame | undefined;
    };
};

export function startSession({ session, auth }: Foundation, { landing, frame }: Screen): () => void {
    const lifetime = createLifetime();
    const inside = () => session.status() === "signed-in" && session.user()?.verified !== false;
    /**
     * The frame opens for an account the API will answer.
     *
     * Signed in is no longer enough: an account whose address has not answered
     * its code reaches the auth endpoints and nothing else, so opening the app
     * for one would be opening an app in which every request fails. It stays on
     * the landing, where the card is showing it the code form, until it does.
     *
     * The frame arrives with the session (decision 0002): asked for here if it
     * is not here yet, and the landing lifts once it is. A returning listener
     * has it asked for at boot, below, so it is there before the restore is.
     */
    function settle(): void {
        if (!inside()) {
            landing.show(true);
            return;
        }
        // signed in, with the frame still to come (a visitor's sign-in): the
        // card says so rather than sitting there as if the click had not landed
        if (!frame.current()) landing.setBusy(true);
        void frame.ensure().then((mounted) => {
            if (lifetime.signal.aborted || !inside()) return;
            landing.setBusy(false);
            if (!mounted) {
                // the chunk did not arrive: the card is at least something to hold on to
                landing.show(true);
                return;
            }
            landing.show(false);
            mounted.worldmap.ensure();
        });
    }

    lifetime.onDestroy(session.store.on("status", (status) => {
        // "signing-in" changes nothing on screen: a returning listener keeps the
        // frame, a visitor keeps the landing; only the outcome moves the landing
        if (status === "signed-in") rememberSession(localStorage);
        if (status === "signed-in" || status === "signed-out") settle();
    }));
    // a sign-out or an account switch: the next boot is a visitor's until somebody signs in
    lifetime.onDestroy(session.onBoundary(() => forgetSession(localStorage)));
    // and the moment the code is accepted, without another sign-in
    lifetime.onDestroy(session.store.on("user", () => {
        if (session.status() === "signed-in") settle();
    }));

    // Boot: the refresh cookie may sign us back in. A browser that expects a
    // session (the marker a sign-in leaves, core/session-marker.ts) gets no
    // landing card while the session is restored (it shows only if the restore
    // ends signed out), and the frame and the Mapbox chunk are fetched
    // meanwhile, so both are there at sign-in. A refused restore takes the
    // marker away: that guess is paid once, not at every reload.
    //
    // A browser without the marker is a visitor's: the restore is not tried.
    // The marker is written at every sign-in and taken away at every sign-out,
    // so the cookie without it is the odd case (site data cleared by hand), and
    // that person signs in again. Trying it cost every visitor a request
    // answered 401, an error in the console of a page that had done nothing wrong.
    const returning = expectsSession(localStorage);
    landing.show(!returning);
    if (!returning) return lifetime.destroy;
    landing.setBusy(true);
    preloadMapbox();
    void frame.ensure();
    void auth.restore().finally(() => {
        if (lifetime.signal.aborted) return;
        if (!inside()) forgetSession(localStorage);
        landing.setBusy(false);
    });
    return lifetime.destroy;
}
